/**
 * V208 / Phase E-4 — Operator Console.
 *
 * Runtime triage surface for users with the Operator role grant.
 * Operator is the "fingers on the controls" role: SRE / on-call rotations
 * who need to see what's running, kill anything misbehaving, and trigger
 * remediation workflows — without admin privilege escalation.
 *
 * Three panels:
 *   1. Live execution feed — recent executions across the operator's
 *      scope, with status, mode, and duration. Drill-down opens the
 *      existing execution detail flow.
 *   2. Kill switch grid — every agent in scope with a one-click kill-now
 *      action. Backend gates on inference.killswitch (Operator holds it).
 *   3. Workflow launcher — surfaces the top-level workflows the Operator
 *      can trigger on demand (workflow.execute permission).
 *
 * No write surface on agents/policies/skills — those stay with the
 * authoring tiers. Operator's value is operational speed, not config
 * authority.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, AlertTriangle, RefreshCw, Power, Play, ChevronRight, Zap, Clock,
} from 'lucide-react';

import { V2PageShell } from '../components/v2/V2PageShell';
import { Card } from '../components/v2/Card';
import { Button } from '../components/v2/Button';
import { Pill } from '../components/v2/Pill';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../services/api';

const STATUS_TONE = {
  PENDING:        'warning',
  RUNNING:        'action',
  APPROVED:       'success',
  REJECTED:       'danger',
  COMPLETED:      'success',
  POLICY_BLOCKED: 'danger',
  ERROR:          'danger',
  FAILED:         'danger',
};

export default function OperatorConsolePageV2() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { hasPermission } = useAuth();

  const [agents, setAgents] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const canKill = hasPermission && hasPermission('inference.killswitch');
  const canExecute = hasPermission && hasPermission('workflow.execute');

  // ── Initial load + manual refresh ───────────────────────────────
  // Verified exports in services/api.js (V208 Phase E-4 build-fix):
  //   • getAgents({orgId,tenantId})  — agent list, optionally scoped
  //   • getWorkflows({orgId,tenantId,agentId,archived}) — workflow list
  //   • getExecutions(agentId)       — per-agent executions (no global feed)
  // No global "recent executions" endpoint exists today — the live-feed
  // panel falls back to executions of the FIRST few agents in scope, which
  // is fine for the initial Operator Console pass. A dedicated
  // /api/v1/executions/recent endpoint is a Phase D-5 follow-up.
  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [agentsRes, workflowsRes] = await Promise.allSettled([
        api.getAgents({}),
        api.getWorkflows({}),
      ]);

      const unwrap = (res) => {
        if (res.status === 'rejected' || !res.value) return [];
        const v = res.value;
        if (v && v.ok === false) return [];
        const payload = Object.prototype.hasOwnProperty.call(v, 'data') ? v.data : v;
        return Array.isArray(payload) ? payload : [];
      };

      const agentList = unwrap(agentsRes);
      const workflowList = unwrap(workflowsRes);
      setAgents(agentList);
      setWorkflows(workflowList);

      // Build a "recent executions" view by fetching executions from the
      // first 5 agents — bounded fan-out so we don't N+1 the API for orgs
      // with hundreds of agents. Caller can refresh manually.
      const topAgents = agentList.slice(0, 5);
      if (topAgents.length > 0) {
        const execResults = await Promise.allSettled(
          topAgents.map((a) => api.getExecutions(a.id))
        );
        const flat = execResults.flatMap((r) => {
          if (r.status !== 'fulfilled' || !r.value) return [];
          const v = r.value;
          if (v && v.ok === false) return [];
          const payload = Object.prototype.hasOwnProperty.call(v, 'data') ? v.data : v;
          return Array.isArray(payload) ? payload : [];
        });
        // Sort by createdAt desc, cap at 25
        flat.sort((a, b) => {
          const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return tb - ta;
        });
        setExecutions(flat.slice(0, 25));
      } else {
        setExecutions([]);
      }
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ── Kill switch ─────────────────────────────────────────────────
  // VERIFIED 2026-05-20: there is no current backend endpoint for the
  // per-agent kill switch. `inference.killswitch` exists as a permission
  // string in RbacService (granted to Operator, Tenant Admin, etc.) and
  // gates a future controller route. Until that controller lands, the
  // Kill button surfaces a clear "endpoint pending" toast rather than
  // calling a non-existent helper. Phase D-5 (follow-up) wires the
  // PUT /api/v1/agents/{id}/inference-mode endpoint that this calls.
  const handleKill = async (agent) => {
    if (!canKill) return;
    addToast({
      title: 'Kill switch — endpoint pending',
      message: 'Backend kill-switch endpoint lands in Phase D-5. Permission gate verified; controller wiring deferred.',
      tone: 'warning',
    });
  };

  // ── Run workflow ────────────────────────────────────────────────
  const handleRun = async (workflow) => {
    if (!canExecute) return;
    const workflowId = workflow.id || workflow.workflowId;
    if (!workflowId) {
      addToast({ title: 'Run failed', message: 'Workflow missing id', tone: 'danger' });
      return;
    }
    try {
      // VERIFIED: api.executeWorkflow(workflowId, payload) exists. Empty
      // payload triggers the workflow with default inputs.
      const res = await api.executeWorkflow(workflowId, {});
      if (res && res.ok === false) {
        addToast({ title: 'Run failed', message: res.error || 'Unable to run', tone: 'danger' });
      } else {
        addToast({ title: 'Workflow started', message: `${workflow.name || workflowId} queued`, tone: 'success' });
      }
    } catch (e) {
      addToast({ title: 'Run failed', message: e?.message || String(e), tone: 'danger' });
    }
  };

  // ── KPI strip ───────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const running = executions.filter((e) => 'RUNNING' === (e.status || '').toUpperCase()).length;
    const pending = executions.filter((e) => 'PENDING' === (e.status || '').toUpperCase()).length;
    const blocked = executions.filter((e) => 'POLICY_BLOCKED' === (e.status || '').toUpperCase()).length;
    const errors  = executions.filter((e) => ['ERROR','FAILED','REJECTED'].includes((e.status || '').toUpperCase())).length;
    return { running, pending, blocked, errors, agents: agents.length, workflows: workflows.length };
  }, [agents, executions, workflows]);

  return (
    <V2PageShell
      title="Operator Console"
      subtitle="Runtime triage — kill switch, live execution feed, workflow launcher."
      backTo="/overview"
      backLabel="Back to Overview"
      tags={<Pill tone="cyan" size="sm" dot>Operator</Pill>}
      actions={
        <Button
          variant="secondary"
          size="sm"
          iconLeft={<RefreshCw size={14} />}
          onClick={load}
          disabled={loading}
        >Refresh</Button>
      }
    >
      {error && (
        <Card padding="sm" tone="danger" className="mb-4">
          <div className="flex items-center gap-2 text-v2-danger text-[13px]">
            <AlertTriangle size={14} />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
        <KpiCard label="Agents in scope" value={kpis.agents} icon={<Activity size={14} />} />
        <KpiCard label="Running" value={kpis.running} tone="action" icon={<Zap size={14} />} />
        <KpiCard label="Pending" value={kpis.pending} tone="warning" icon={<Clock size={14} />} />
        <KpiCard label="Blocked" value={kpis.blocked} tone="danger" icon={<AlertTriangle size={14} />} />
        <KpiCard label="Errors" value={kpis.errors} tone="danger" icon={<AlertTriangle size={14} />} />
        <KpiCard label="Workflows" value={kpis.workflows} icon={<Play size={14} />} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* ── Kill switch grid ───────────────────────────────────── */}
        <Card padding="md">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[14px] font-semibold text-v2-ink">Kill switch</div>
            <Pill tone="muted" size="sm">{agents.length} agents</Pill>
          </div>
          {agents.length === 0 && !loading && (
            <div className="text-[12px] text-v2-ink-muted">No agents in scope.</div>
          )}
          <div className="space-y-1.5 max-h-[380px] overflow-y-auto">
            {agents.map((a) => (
              <div key={a.id} className="flex items-center gap-2 p-2 rounded-v2-sm hover:bg-v2-muted">
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-medium text-v2-ink truncate">{a.name}</div>
                  <div className="text-[11px] text-v2-ink-muted truncate">
                    {a.phase ? `Phase ${a.phase}` : '—'}
                    {a.functionMode ? ` · ${a.functionMode}` : ''}
                  </div>
                </div>
                <Button
                  variant="secondary"
                  size="xs"
                  iconLeft={<Power size={12} />}
                  disabled={!canKill}
                  onClick={() => handleKill(a)}
                  title={canKill ? 'Kill agent (action endpoint not yet available)' : 'inference.killswitch permission required'}
                >Kill</Button>
              </div>
            ))}
          </div>
        </Card>

        {/* ── Workflow launcher ──────────────────────────────────── */}
        <Card padding="md">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[14px] font-semibold text-v2-ink">Workflow launcher</div>
            <Pill tone="muted" size="sm">{workflows.length} workflows</Pill>
          </div>
          {workflows.length === 0 && !loading && (
            <div className="text-[12px] text-v2-ink-muted">No runnable workflows in scope.</div>
          )}
          <div className="space-y-1.5 max-h-[380px] overflow-y-auto">
            {workflows.map((w) => (
              <div key={w.id || w.workflowId} className="flex items-center gap-2 p-2 rounded-v2-sm hover:bg-v2-muted">
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-medium text-v2-ink truncate">{w.name || w.workflowId}</div>
                  <div className="text-[11px] text-v2-ink-muted truncate">
                    {w.description || w.taskQueue || '—'}
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="xs"
                  iconLeft={<Play size={12} />}
                  disabled={!canExecute}
                  onClick={() => handleRun(w)}
                  title={canExecute ? 'Run workflow' : 'workflow.execute permission required'}
                >Run</Button>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ── Live execution feed ──────────────────────────────────── */}
      <Card padding="md" className="mt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[14px] font-semibold text-v2-ink">Recent executions</div>
          <Pill tone="muted" size="sm">{executions.length} shown</Pill>
        </div>
        {executions.length === 0 && !loading && (
          <div className="text-[12px] text-v2-ink-muted">No recent executions in scope.</div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-v2-ink-muted border-b border-v2-border-subtle">
                <th className="py-1.5 pr-3 font-semibold">Status</th>
                <th className="py-1.5 pr-3 font-semibold">Agent</th>
                <th className="py-1.5 pr-3 font-semibold">Skill</th>
                <th className="py-1.5 pr-3 font-semibold">Mode</th>
                <th className="py-1.5 pr-3 font-semibold text-right">Duration</th>
                <th className="py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {executions.map((e) => {
                const status = (e.status || '').toUpperCase();
                return (
                  <tr key={e.id} className="border-b border-v2-border-subtle hover:bg-v2-muted">
                    <td className="py-1.5 pr-3">
                      <Pill tone={STATUS_TONE[status] || 'muted'} size="sm">{status || '—'}</Pill>
                    </td>
                    <td className="py-1.5 pr-3 truncate max-w-[180px]">{e.agentName || e.agentId?.slice(0,8) || '—'}</td>
                    <td className="py-1.5 pr-3 truncate max-w-[140px]">{e.skillName || '—'}</td>
                    <td className="py-1.5 pr-3 truncate max-w-[100px]">{e.mode || '—'}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {e.durationMs != null ? `${e.durationMs} ms` : '—'}
                    </td>
                    <td className="py-1.5">
                      <button
                        type="button"
                        onClick={() => navigate(`/agents/${e.agentId}?tab=executions&exec=${e.id}`)}
                        className="text-v2-action hover:underline text-[11.5px] inline-flex items-center gap-0.5"
                      >Open <ChevronRight size={11} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </V2PageShell>
  );
}

function KpiCard({ label, value, tone, icon }) {
  return (
    <Card padding="sm">
      <div className="flex items-center gap-1.5">
        {icon && <span className={`text-v2-${tone || 'ink-muted'}`}>{icon}</span>}
        <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold">{label}</div>
      </div>
      <div className={`text-[20px] font-semibold mt-0.5 ${tone ? `text-v2-${tone}` : 'text-v2-ink'}`}>
        {value}
      </div>
    </Card>
  );
}
